
import { DiaryEntry } from "@/utils/diary";
import { memo, useMemo } from "react";
import EntryCardsList from "./EntryCardsList";
import EmptyState from "./EmptyState";

interface EntriesListProps {
  entries: DiaryEntry[];
  onDelete: (id: string) => void;
  onUpdate: (updatedEntry: DiaryEntry) => void;
  searchTerm: string;
  selectedDate: Date | undefined;
  isLoading: boolean;
}

// Use memo to prevent unnecessary re-renders
const EntriesList = memo(({ 
  entries, 
  onDelete, 
  onUpdate, 
  searchTerm, 
  selectedDate,
  isLoading
}: EntriesListProps) => {
  
  // Memoize filtered entries to prevent recomputation on each render
  const filteredEntries = useMemo(() => {
    // If loading, return empty array to avoid unnecessary filtering
    if (isLoading) return [];
    
    return entries.filter(entry => {
      // Search term filtering
      const matchesSearch = !searchTerm || 
        `${entry.babyName || ''} ${entry.entryText || ''} ${entry.mood || ''}`.toLowerCase()
          .includes(searchTerm.toLowerCase());
      
      // Date filtering
      if (selectedDate && matchesSearch) {
        const entryDate = entry.date ? new Date(entry.date) : new Date(entry.createdAt);
        const selectedDateStr = selectedDate.toISOString().substring(0, 10);
        return entryDate.toISOString().substring(0, 10) === selectedDateStr;
      }
      
      return matchesSearch;
    });
  }, [entries, searchTerm, selectedDate, isLoading]);
  
  // Show loading state
  if (isLoading) {
    return (
      <EntryCardsList 
        entries={[]}
        onDelete={onDelete}
        onUpdate={onUpdate}
        isLoading={true}
      />
    );
  }
  
  // Show entries if there are any after filtering
  if (filteredEntries.length > 0) {
    return (
      <div className="space-y-6">
        <EntryCardsList 
          entries={filteredEntries}
          onDelete={onDelete}
          onUpdate={onUpdate}
        />
      </div>
    );
  }
  
  // Show empty state
  return <EmptyState searchTerm={searchTerm} selectedDate={selectedDate} />;
});

EntriesList.displayName = "EntriesList";

export default EntriesList;
